import React, { useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Box, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import ScreenTitle from '../components/ScreenTitle';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at center, #0a0a0a 0%, #000000 100%);
  position: relative;
`;

const InfoOverlay = styled.div`
  position: absolute;
  top: 40px;
  left: 40px;
  z-index: 10;
  pointer-events: none;
`;


const Subtitle = styled.p`
  color: var(--light-gray);
  font-size: 1.125rem;
  opacity: 0.8;
`;

const DataPanel = styled.div`
  position: absolute;
  bottom: 40px;
  right: 40px;
  background: rgba(26, 26, 26, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  min-width: 300px;
  z-index: 10;
`;

const DataItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  
  span:first-of-type {
    color: var(--gray);
    font-size: 0.875rem;
  }
  
  span:last-child {
    color: ${props => props.color || 'var(--white)'};
    font-weight: 600;
  }
`;

// 3D Kuutio Component
function KuutioCube({ activeScenario }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.2;
      meshRef.current.rotation.y += delta * 0.3;
    }
  });

  const getFaceColor = (index) => {
    const colors = [
      '#0099ff', // Data - Blue
      '#ffcc00', // Futures - Yellow  
      '#ff0066', // Human - Pink
      '#00ff88', // Solutions - Green
      '#9933ff', // Top - Purple
      '#ff6600', // Bottom - Orange
    ];
    return colors[index % colors.length];
  };

  return (
    <group>
      <mesh
        ref={meshRef}
        scale={hovered ? 2.2 : 2}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <boxGeometry args={[1, 1, 1]} />
        <MeshDistortMaterial
          color={getFaceColor(activeScenario)}
          roughness={0.1}
          metalness={0.8}
          distort={0.2}
          speed={2}
          envMapIntensity={1}
        />
      </mesh>
      
      {/* Floating particles around cube */}
      {[...Array(20)].map((_, i) => (
        <mesh
          key={i}
          position={[
            Math.sin(i * 0.5) * 3,
            Math.cos(i * 0.7) * 3,
            Math.sin(i * 0.3) * 3
          ]}
        >
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshBasicMaterial 
            color={getFaceColor(i)} 
            emissive={getFaceColor(i)}
            emissiveIntensity={2}
          />
        </mesh>
      ))}
    </group>
  );
}

// Grid plane
function GridPlane() {
  return (
    <gridHelper 
      args={[20, 20, '#0099ff', '#003366']} 
      position={[0, -3, 0]}
      rotation={[0, 0, 0]}
    />
  );
}

// Main Component
function CentralTable() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [scenarioData, setScenarioData] = useState({
    climate: 0.7,
    economy: 0.4,
    technology: 0.8,
    social: 0.6,
    political: 0.5
  });

  useEffect(() => {
    // Simulate real-time data updates
    const interval = setInterval(() => {
      setScenarioData(prev => ({
        climate: Math.min(1, Math.max(0, prev.climate + (Math.random() - 0.5) * 0.1)),
        economy: Math.min(1, Math.max(0, prev.economy + (Math.random() - 0.5) * 0.1)),
        technology: Math.min(1, Math.max(0, prev.technology + (Math.random() - 0.5) * 0.1)),
        social: Math.min(1, Math.max(0, prev.social + (Math.random() - 0.5) * 0.1)),
        political: Math.min(1, Math.max(0, prev.political + (Math.random() - 0.5) * 0.1))
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getValueColor = (value) => {
    if (value > 0.7) return 'var(--kuutio-green)';
    if (value > 0.4) return 'var(--kuutio-yellow)';
    return 'var(--kuutio-pink)';
  };

  return (
    <Container>
      <InfoOverlay>
        <ScreenTitle>
          KUUTIO
        </ScreenTitle>
        <Subtitle>Operaatiohuone · Skenaario-analyysi käynnissä</Subtitle>
      </InfoOverlay>

      <Canvas 
        camera={{ position: [0, 5, 10], fov: 60 }}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={0.2} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#0099ff" />
        <spotLight position={[0, 10, 0]} angle={0.3} penumbra={1} intensity={1} />
        
        <KuutioCube activeScenario={activeScenario} />
        <GridPlane />
        
        <OrbitControls 
          enablePan={false}
          minDistance={5}
          maxDistance={20}
          autoRotate
          autoRotateSpeed={0.5}
        />
        
        <fog attach="fog" args={['#000000', 10, 30]} />
      </Canvas>

      <DataPanel>
        <h4 style={{ marginBottom: '16px', color: 'var(--white)' }}>
          Skenaarion parametrit
        </h4>
        <DataItem color={getValueColor(scenarioData.climate)}>
          <span>Ilmasto</span>
          <span>{(scenarioData.climate * 100).toFixed(0)}%</span>
        </DataItem>
        <DataItem color={getValueColor(scenarioData.economy)}>
          <span>Talous</span>
          <span>{(scenarioData.economy * 100).toFixed(0)}%</span>
        </DataItem>
        <DataItem color={getValueColor(scenarioData.technology)}>
          <span>Teknologia</span>
          <span>{(scenarioData.technology * 100).toFixed(0)}%</span>
        </DataItem>
        <DataItem color={getValueColor(scenarioData.social)}>
          <span>Yhteiskunta</span>
          <span>{(scenarioData.social * 100).toFixed(0)}%</span>
        </DataItem>
        <DataItem color={getValueColor(scenarioData.political)}>
          <span>Politiikka</span>
          <span>{(scenarioData.political * 100).toFixed(0)}%</span>
        </DataItem>
      </DataPanel>
    </Container>
  );
}

export default CentralTable;